import { useEffect, useRef } from 'react'
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore'
import toast from 'react-hot-toast'
import { db, COLLECTIONS } from '@/lib/firebase'
import { useAuthStore } from '@/stores/authStore'
import { showBrowserNotification } from '@/lib/messaging'
import { formatCurrency } from '@/lib/utils'

/**
 * Real-time order alerts while admin panel is open (Firestore listener).
 * Complements FCM for foreground reliability.
 */
export function useAdminOrderAlerts() {
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const initialLoad = useRef(true)

  useEffect(() => {
    if (!isAdmin) return

    const q = query(
      collection(db, COLLECTIONS.orders),
      orderBy('createdAt', 'desc'),
      limit(5)
    )

    const unsub = onSnapshot(
      q,
      (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type !== 'added') continue
          if (initialLoad.current) continue

          const data = change.doc.data()
          const orderId = change.doc.id
          const orderNumber = (data.orderNumber as string) || orderId
          const title = 'New order placed'
          const body = `${orderNumber} · ${data.customerName || 'Customer'} · ${formatCurrency(Number(data.total) || 0)}`
          const link = `/admin/orders/${orderId}`

          toast.success(`${title} — ${body}`, { duration: 8000 })
          void showBrowserNotification(title, body, { orderId, link })
        }
        initialLoad.current = false
      },
      (err) => console.error('Order alerts listener error', err)
    )

    return () => {
      initialLoad.current = true
      unsub()
    }
  }, [isAdmin])
}
